import { MouseEvent } from 'react';

import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';

import Icon from 'src/common/components/icons/Icon';
import TicketCard from 'src/common/components/styled/TicketCard';
import TicketCardEditDeleteFooter from 'src/common/components/styled/TicketCardEditDeleteFooter';
import { LanguageKeys } from 'src/common/constants/languages';
import { getTechnicalTypeDescription } from 'src/helpers/technicalTypeDescription';
import { TechnicalType } from 'src/interfaces/TechnicalType';

interface Props {
  item: TechnicalType & {
    icon?: string;
  };
  eyebrow?: string;
  defaultIcon?: string;
  /** When false, hides the description line (distances often have no description text). */
  showDescription?: boolean;
  /** Name beside the avatar; hides section eyebrow and duplicate title row. */
  titleBesideAvatar?: boolean;
  onDelete: (id: string, e?: MouseEvent<unknown>) => void;
  onEdit: (id: string, e?: MouseEvent<unknown>) => void;
}

const TechnicalTypeCard = ({
  item,
  eyebrow,
  defaultIcon,
  showDescription = true,
  titleBesideAvatar = false,
  onDelete,
  onEdit,
}: Props) => {
  const { i18n, icon, id, name, published } = item;
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { palette } = useTheme();

  const cardDescription = showDescription
    ? getTechnicalTypeDescription(item, currentLang) ?? i18n[currentLang]
    : undefined;

  return (
    <TicketCard
      eyebrow={titleBesideAvatar ? undefined : eyebrow}
      title={name}
      titleBesideAvatar={titleBesideAvatar}
      description={cardDescription}
      avatar={
        <Avatar
          sx={{
            background: `linear-gradient(135deg, ${palette.success.dark} 0%, ${palette.success.light} 100%)`,
          }}
          aria-label={name}
        >
          {icon ? (
            <Icon icon={icon} fontSize="large" />
          ) : defaultIcon ? (
            <Icon icon={defaultIcon} fontSize="large" />
          ) : (
            name.charAt(0)
          )}
        </Avatar>
      }
      footer={
        <TicketCardEditDeleteFooter
          popupId={`technical-type-tools-${id}`}
          optionsAriaLabel={t('technicalTypes.optionsLabel')}
          editLabel={t('technicalTypes.edit')}
          deleteLabel={t('technicalTypes.delete')}
          onEdit={e => onEdit(id, e)}
          onDelete={e => onDelete(id, e)}
        />
      }
      badges={[
        {
          label:
            published !== false
              ? t('system.sponsors.published')
              : t('system.sponsors.unpublished'),
          highlighted: published !== false,
        },
      ]}
    />
  );
};

export default TechnicalTypeCard;
